import { cn } from "@/lib/utils";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useMediaQuery } from "usehooks-ts";

import * as privateApi from "../../api/privateApi";
import { ToggleSwitch } from "../../components/atoms/ToggleSwitch/ToggleSwitch";
import PremiumFeatures from "../../components/organisms/PremiumFeatures/PremiumFeatures";
import SubscriptionCard from "../../components/organisms/SubscriptionCard/SubscriptionCard";
import { showModal } from "../../redux/message/messageSlice";
import { AppDispatch } from "../../redux/store";
import {
  Subscription as SubscriptionType,
  userPricesSelector,
} from "../../redux/user/userSlice";
import { setBuyIntent } from "../../redux/user/userSlice";
import { BREAKPOINTS } from "../../settings";
import "./Subscription.css";

type SubscriptionProps = {
  isLoggedIn: boolean;
  subscription: SubscriptionType;
};

type LinkState = {
  from: string;
};

const Subscription = ({ isLoggedIn, subscription }: SubscriptionProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const hasUltimateSub = subscription === "diamond";
  const hasEliteSub = subscription === "gold";
  const hasProSub = subscription === "silver";
  const location = useLocation();
  const { state } = location;
  const from = (state as LinkState)?.from;
  const clubImportRef = useRef(null);
  const [yearlySubActive, setYearlySubActive] = useState(true);
  const [hasSub, setHasSub] = useState(false);
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const prices = useSelector(userPricesSelector);
  const [priceId, setPriceId] = useState<string>("");

  useEffect(() => {
    setHasSub(hasUltimateSub || hasEliteSub || hasProSub);
  }, [hasEliteSub, hasProSub, hasUltimateSub]);

  useEffect(() => {
    if (from === "import") {
      clubImportRef.current.scrollIntoView();
    } else {
      window.scrollTo(0, 0);
    }
  }, [from]);

  const [selectedSubscription, setSelectedSubscription] = useState<
    "Elite" | "Pro" | "Ultimate"
  >("Ultimate");

  const handleSubscriptionClicked = (sub: "Elite" | "Pro" | "Ultimate") => {
    setSelectedSubscription(sub);
  };

  useEffect(() => {
    switch (selectedSubscription) {
      case "Pro":
        setPriceId(yearlySubActive ? proYearlyPrice?.id : proPrice?.id);
        break;
      case "Elite":
        setPriceId(yearlySubActive ? eliteYearlyPrice?.id : elitePrice?.id);
        break;
      case "Ultimate":
        setPriceId(
          yearlySubActive ? ultimateYearlyPrice?.id : ultimatePrice?.id,
        );
    }
  }, [selectedSubscription, prices, yearlySubActive]);

  const getSubscriptionClicked = () => {
    if (isLoggedIn) {
      if (hasSub) {
        privateApi.createCustomerPortalSession();
      } else {
        privateApi.createCheckoutSession({
          priceId,
          freeTrial: 0,
        });
      }
    } else {
      dispatch(showModal({ modalType: "sign-in-to-subscribe" }));
      dispatch(setBuyIntent({ subscriptionId: priceId }));
    }
  };

  const proPrice = prices.find(
    (price) => price.subscription === "silver" && price.interval === "monthly",
  );
  const elitePrice = prices.find(
    (price) => price.subscription === "gold" && price.interval === "monthly",
  );
  const ultimatePrice = prices.find(
    (price) => price.subscription === "diamond" && price.interval === "monthly",
  );
  const proYearlyPrice = prices.find(
    (price) => price.subscription === "silver" && price.interval === "yearly",
  );
  const eliteYearlyPrice = prices.find(
    (price) => price.subscription === "gold" && price.interval === "yearly",
  );
  const ultimateYearlyPrice = prices.find(
    (price) => price.subscription === "diamond" && price.interval === "yearly",
  );

  const subscriptions = (
    <div
      className={"flex touch-device:flex-row justify-center gap-x-16 text-left"}
    >
      {(!isMobile || selectedSubscription === "Pro") && (
        <SubscriptionCard
          currency={proPrice?.currency}
          selected={selectedSubscription === "Pro"}
          activeSubscription={hasProSub}
          onClick={handleSubscriptionClicked}
          monthlyPrice={
            yearlySubActive
              ? proYearlyPrice?.monthlyPrice
              : proPrice?.monthlyPrice
          }
          yearlyPrice={proYearlyPrice?.yearlyPrice}
          tier={"Pro"}
          {...(yearlySubActive
            ? { annualDiscount: proYearlyPrice?.savings }
            : {})}
        />
      )}
      {(!isMobile || selectedSubscription === "Ultimate") && (
        <SubscriptionCard
          currency={ultimatePrice?.currency}
          selected={selectedSubscription === "Ultimate"}
          activeSubscription={hasUltimateSub}
          onClick={handleSubscriptionClicked}
          monthlyPrice={
            yearlySubActive
              ? ultimateYearlyPrice?.monthlyPrice
              : ultimatePrice?.monthlyPrice
          }
          yearlyPrice={ultimateYearlyPrice?.yearlyPrice}
          tier={"Ultimate"}
          {...(yearlySubActive
            ? { annualDiscount: ultimateYearlyPrice?.savings }
            : {})}
          mostPopular={true}
        />
      )}

      {(!isMobile || selectedSubscription === "Elite") && (
        <SubscriptionCard
          currency={elitePrice?.currency}
          selected={selectedSubscription === "Elite"}
          activeSubscription={hasEliteSub}
          onClick={handleSubscriptionClicked}
          monthlyPrice={
            yearlySubActive
              ? eliteYearlyPrice?.monthlyPrice
              : elitePrice?.monthlyPrice
          }
          yearlyPrice={eliteYearlyPrice?.yearlyPrice}
          tier={"Elite"}
          {...(yearlySubActive
            ? { annualDiscount: eliteYearlyPrice?.savings }
            : {})}
        />
      )}
    </div>
  );

  const HugeButton = ({ children }: { children?: React.ReactNode }) => (
    <button
      className="text-lg touch-device:text-2xl text-white bg-primary-blue touch-device:hover:bg-primary-blue/90 w-[263px] touch-device:w-[333px] rounded-lg mx-auto py-[9.6px] font-semibold large-2:mt-8 touch-device:mt-4"
      style={{
        boxShadow:
          "0px -1.8px 0px 0px rgba(47, 43, 67, 0.30) inset, 0px 2.4px 4.8px 0px rgba(0, 0, 0, 0.25)",
      }}
      onClick={(e) => {
        e.stopPropagation();
        getSubscriptionClicked();
      }}
    >
      {children}
    </button>
  );

  const MobileSelectSubscriptionButton = ({
    children,
    onClick,
    mostPopular,
    active,
  }: {
    children: React.ReactNode;
    onClick: () => void;
    mostPopular?: boolean;
    active?: boolean;
  }) => (
    <button
      className={clsx(
        "rounded-[44px] text-black w-full h-[32px] relative font-medium text-lg",
        active ? "bg-white" : "bg-white/50",
      )}
      onClick={onClick}
    >
      {children}
      {mostPopular && (
        <div
          className={clsx(
            "absolute -top-1.5 right-1 rounded-full bg-primary-blue p-[1px]",
            !active && "brightness-75",
          )}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="M8.00033 0.666992L9.64676 5.66989H14.9747L10.6643 8.76186L12.3108 13.7648L8.00033 10.6728L3.6899 13.7648L5.33634 8.76186L1.02591 5.66989H6.35389L8.00033 0.666992Z"
              fill="white"
            />
          </svg>
        </div>
      )}
    </button>
  );

  let hugeButtonTitle: string;
  if (selectedSubscription === "Pro") {
    if (hasSub) {
      hugeButtonTitle = "Manage Plan";
    } else {
      hugeButtonTitle = "Get EasySBC Pro";
    }
  } else if (selectedSubscription === "Elite") {
    if (hasProSub) {
      hugeButtonTitle = "Upgrade Plan";
    } else if (hasEliteSub || hasUltimateSub) {
      hugeButtonTitle = "Manage Plan";
    } else {
      hugeButtonTitle = "Get EasySBC Elite";
    }
  } else if (selectedSubscription === "Ultimate") {
    if (hasEliteSub || hasProSub) {
      hugeButtonTitle = "Upgrade Plan";
    } else if (hasUltimateSub) {
      hugeButtonTitle = "Manage Plan";
    } else {
      hugeButtonTitle = "Get EasySBC Ultimate";
    }
  }

  const bottomPart = (
    <div className="flex flex-col items-center text-white gap-y-5">
      {isMobile && (
        <div className="flex flex-row w-[308px] justify-between gap-x-2">
          <MobileSelectSubscriptionButton
            onClick={() => handleSubscriptionClicked("Pro")}
            active={selectedSubscription === "Pro"}
          >
            Pro
          </MobileSelectSubscriptionButton>
          <MobileSelectSubscriptionButton
            onClick={() => handleSubscriptionClicked("Ultimate")}
            active={selectedSubscription === "Ultimate"}
            mostPopular={true}
          >
            Ultimate
          </MobileSelectSubscriptionButton>
          <MobileSelectSubscriptionButton
            onClick={() => handleSubscriptionClicked("Elite")}
            active={selectedSubscription === "Elite"}
          >
            Elite
          </MobileSelectSubscriptionButton>
        </div>
      )}
      <HugeButton>{hugeButtonTitle}</HugeButton>
      <p className="max-w-[570px] text-center text-sm touch-device:text-base">
        Subscription continues until cancelled. Cancel anytime.
      </p>
    </div>
  );

  return (
    <main className="flex flex-col items-center gap-y-6">
      <Helmet>
        <title>
          EA FC 25 - Give your Ultimate Team the power of AI - EasySBC
        </title>
        <meta
          name="description"
          content="Join thousands of other EasySBC subscribers - Combine your club with our AI to solve any SBC in seconds, build the most meta teams for Weekend League, and discover profitable trades with one click."
        />
        <link rel="canonical" href="https://www.easysbc.io/subscription" />
      </Helmet>

      <>
        <div
          className={cn(
            "absolute right-0 left-0 bg-linear-to-b to-[#141414] from-[#1C2A37] bottom-0 -z-10 touch-device:top-[50px]",
            {
              /* TODO: Remember to update this when new feature banner is added */
            },
          )}
        />
        <h1 className="text-white text-[24px] touch-device:text-[30px] large-2:text-[36px] pt-4 touch-device:pt-4 large-2:pt-16 text-center px-4 large-2:pb-8 hidden md-lg:flex">
          Do SBCs fast. Build winning squads. Have more fun.
        </h1>
        <div className="flex flex-row mt-4 text-sm text-white md-lg:mb-2 md-lg:mt-0 gap-x-3 touch-device:text-base large-2:text-lg large-2:pb-2">
          <div className="flex flex-col gap-y-1.5">
            <div className="flex mx-auto gap-x-2">
              <p
                className={clsx(
                  yearlySubActive && "text-gray-300",
                  "font-normal",
                )}
              >
                Monthly
              </p>
              <div className="my-auto">
                <ToggleSwitch
                  onChange={() => setYearlySubActive((prev) => !prev)}
                  isActive={yearlySubActive}
                />
              </div>
              <p
                className={clsx(
                  !yearlySubActive && "text-gray-300",
                  "font-semibold",
                )}
              >
                Yearly
              </p>
            </div>
            <p className="my-auto text-lg font-light desktop:text-xl touch-device:text-2xl text-white/50">
              Get 4 months FREE with yearly plan.
            </p>
          </div>
        </div>
      </>

      {subscriptions}
      {bottomPart}
      <div ref={clubImportRef}>
        <PremiumFeatures />
      </div>
    </main>
  );
};

export default Subscription;
